import React, { useEffect, useRef, useState } from 'react';
import { carteraApi } from './api';
import { money } from './helpers';

// Mismo catálogo que RegistrarPagoModal (ese módulo no lo exporta y no se toca
// en este feature, así que se replica). Al corregir se agrega el canal actual
// del pago si viene fuera del catálogo: los importados traen texto libre y no
// queremos "corregirlo" sin que nadie lo pida.
const CANALES = [
  ['transferencia', 'Transferencia'],
  ['spei', 'SPEI'],
  ['domiciliacion', 'Domiciliación'],
  ['efectivo', 'Efectivo'],
];

// Mismo parseo tolerante de RegistrarPagoModal: acepta "26,234", "$26,234.50",
// "26 234". El input es texto (no number) porque el paste con coma truncaba el
// monto ("26,234" → 26.23).
const parseMonto = (s) => {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// "2026-07-27T11:02:31-06:00" / "2026-07-27 11:02:31" → "2026-07-27 11:02".
const fechaHora = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

// "2026-07-28" → "28 de julio". Se arma a mano (sin new Date) porque el string
// viene sin zona y el parseo nativo lo corre un día hacia atrás.
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fechaLarga = (v) => {
  const [, m, d] = String(v || '').slice(0, 10).split('-');
  const mes = MESES[Number(m) - 1];
  return mes ? `${Number(d)} de ${mes}` : String(v || '');
};

// Nombre corto para la frase de arriba ("Es como si Dafne Talnezi nunca…"):
// los clientes vienen en MAYÚSCULAS y con nombre completo, que grita en prosa.
const nombreCliente = (nombre) => {
  const partes = String(nombre || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (partes.length === 0) return 'el cliente';
  return partes.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * Deshacer / corregir / (des)condonar un pago YA registrado.
 *
 * Un solo modal con `modo` (revertir | corregir | condonar), igual que
 * RegistrarPagoModal maneja pago/capital/liquidar. Reusa las clases cm-* para
 * no inventar estilos nuevos.
 *
 * El front NO reimplementa las reglas de negocio: el veredicto viene del
 * preview del back ({puede, motivo}) y los errores 422 se pintan tal cual.
 *
 * props:
 *   credito           resumen del crédito (necesita id, cliente, clave_contrato)
 *   pago              fila de d.pagos (folio, monto_pagado, fecha_pago, canal_pago, notas…)
 *   modoInicial       'revertir' | 'corregir' | 'condonar'
 *   accionCondonacion 'quitar' (ya condonó y se deshace) | 'aplicar' (condonar ahora)
 *   mora              mora acumulada VIVA del crédito (para el texto de "condonar")
 */
export const RevertirPagoModal = ({
  credito, pago, modoInicial = 'revertir', accionCondonacion = 'quitar', mora = 0, onClose, onDone,
}) => {
  const c = credito || {};
  const p = pago || {};
  const creditoId = c.id;
  const pagoId = p.id;

  // Valores originales del pago: base para detectar qué cambió de verdad y
  // mandar SOLO eso en el PATCH (el back exige al menos un campo).
  const montoOrig = Number(p.monto_pagado || 0);
  const fechaOrig = String(p.fecha_pago || '').slice(0, 10);
  const canalOrig = p.canal_pago || '';
  const notasOrig = p.notas || '';

  const [modo, setModo] = useState(modoInicial);
  const [motivo, setMotivo] = useState('');
  // Condonación parcial: '' = perdonar TODA la mora (default, comportamiento
  // original); con texto = perdonar solo ese monto.
  const [parcial, setParcial] = useState('');
  // Se enciende al intentar confirmar sin motivo: pinta el campo de rojo. Antes
  // el botón salía deshabilitado y nada decía POR QUÉ.
  const [motivoErr, setMotivoErr] = useState(false);
  const motivoRef = useRef(null);
  const [monto, setMonto] = useState(montoOrig ? montoOrig.toFixed(2) : '');
  const [fecha, setFecha] = useState(fechaOrig);
  // Se arranca con el canal TAL CUAL viene (aunque sea vacío): si se pusiera un
  // default, `cambiaCanal` sería true de entrada y el PATCH escribiría un dato
  // que el usuario nunca eligió.
  const [canal, setCanal] = useState(canalOrig);
  const [notas, setNotas] = useState(notasOrig);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Deshacer y corregir comparten gates (§4.4 del plan) → el preview sirve para
  // los dos. Condonar tiene gates más laxos (no exige LIFO): ahí no se pide,
  // y si el back lo bloquea llega su 422 al confirmar.
  const usaPreview = modoInicial !== 'condonar';

  useEffect(() => {
    if (!usaPreview || !creditoId || !pagoId) return undefined;
    const ctrl = new AbortController();
    setPreviewLoading(true);
    setError(null);
    carteraApi.reversionPreview(creditoId, pagoId, ctrl.signal)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setPreview(res?.data?.data ?? res?.data ?? null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.message || 'No se pudo calcular el efecto de deshacer este pago.');
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setPreviewLoading(false);
      });
    return () => ctrl.abort();
  }, [usaPreview, creditoId, pagoId]);

  // Veredicto del gate: solo bloquea cuando el back dijo que NO. Si el preview
  // no cargó (red caída, endpoint aún no desplegado) se deja intentar y que el
  // back conteste — nunca al revés.
  const bloqueado = preview != null && preview.puede === false;
  const bloqueoMsg = preview?.motivo || null;

  // ── Corregir: qué cambió ──────────────────────────────────────────────────
  const montoNum = parseMonto(monto);
  const cambiaMonto = Math.abs(montoNum - montoOrig) > 0.004;
  const cambiaFecha = fecha !== fechaOrig;
  const cambiaCanal = canal !== canalOrig;
  const cambiaNotas = notas !== notasOrig;
  // ── Mora al corregir ──────────────────────────────────────────────────────
  // Corregir re-juega el pago contra el estado de AQUEL día, así que la referencia
  // es la mora que había entonces (del snapshot), NO la de hoy. El usuario piensa
  // en "cuánto le cobré", así que el input pide eso y se perdona la diferencia.
  const moraDelDia = Number(preview?.pago?.mora_al_pagar || 0);
  const condonadoOrig = Number(preview?.pago?.mora_condonada || 0);
  const cobradoOrig = Math.max(0, moraDelDia - condonadoOrig);
  const [cobrado, setCobrado] = useState(null); // null = no lo ha tocado
  const cobradoNum = cobrado === null ? cobradoOrig : parseMonto(cobrado);
  const condonarNum = Math.max(0, moraDelDia - cobradoNum);
  const cambiaMora = moraDelDia > 0 && Math.abs(condonarNum - condonadoOrig) > 0.004;

  // Monto, fecha o mora ⇒ el back restaura las filas y vuelve a repartir el dinero.
  // Canal y notas son datos planos: no recalculan nada.
  const reAplica = cambiaMonto || cambiaFecha || cambiaMora;
  const hayCambio = reAplica || cambiaCanal || cambiaNotas;

  // Catálogo del select. Si el pago trae un canal fuera del catálogo (importados
  // con texto libre) se agrega como opción para no pisarlo; si no trae ninguno,
  // se antepone un vacío para que abrir el modal no seleccione nada por sí solo.
  const canales = canalOrig
    ? (CANALES.some(([v]) => v === canalOrig) ? CANALES : [...CANALES, [canalOrig, canalOrig]])
    : [['', '— sin canal —'], ...CANALES];

  const motivoOk = motivo.trim().length > 0;
  const quitando = accionCondonacion === 'quitar';
  const condonaTodo = String(parcial).trim() === '';
  const montoCondonar = condonaTodo ? Number(mora || 0) : parseMonto(parcial);
  const condonadoAqui = Number(p.mora_condonada || 0);

  const titulo = modo === 'corregir'
    ? 'Corregir pago'
    : modo === 'condonar'
      ? (quitando ? 'Quitar condonación de mora' : 'Condonar la mora')
      : 'Deshacer pago';

  // Toda acción exitosa refresca el detalle (load()) y cierra: el estado del
  // crédito cambió y cualquier dato en pantalla ya quedó viejo.
  function terminar() {
    if (onDone) onDone();
    onClose();
  }

  async function confirmarRevertir() {
    if (faltaMotivo()) return;
    setSubmitting(true);
    setError(null);
    try {
      await carteraApi.revertirPago(creditoId, pagoId, { motivo: motivo.trim() });
      terminar();
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo deshacer el pago.');
      setSubmitting(false);
    }
  }

  async function confirmarCorregir() {
    if (faltaMotivo()) return;
    if (!hayCambio) { setError('No cambiaste ningún dato del pago.'); return; }
    if (cambiaMonto && montoNum <= 0) { setError('El monto debe ser mayor a 0.'); return; }
    if (cambiaMora && cobradoNum > moraDelDia + 0.004) {
      setError(`No se puede cobrar más mora de la que había ese día (${money(moraDelDia)}).`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Solo se mandan los campos que de verdad cambiaron: así el back sabe si
      // debe re-aplicar (monto/fecha) o solo actualizar el dato (canal/notas).
      const body = { motivo: motivo.trim() };
      if (cambiaMonto) body.monto = montoNum;
      if (cambiaFecha) body.fecha = fecha;
      if (cambiaCanal) body.canal = canal;
      if (cambiaNotas) body.notas = notas;
      // Se manda lo PERDONADO (lo que el back entiende), aunque la UI pregunte
      // por lo cobrado. 0 = cobrarla toda.
      if (cambiaMora) body.condonar = condonarNum;
      await carteraApi.corregirPago(creditoId, pagoId, body);
      terminar();
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo corregir el pago.');
      setSubmitting(false);
    }
  }

  async function confirmarCondonacion() {
    if (faltaMotivo()) return;
    // Perdón parcial: solo se manda 'monto' si el usuario eligió "solo una parte".
    // Sin la llave, el back perdona toda la mora viva.
    if (!quitando && !condonaTodo) {
      if (montoCondonar <= 0) { setError('Indica cuánto se va a condonar.'); return; }
      if (montoCondonar > Number(mora || 0) + 0.005) {
        setError(`No se puede condonar más de la mora acumulada (${money(mora)}).`);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      await carteraApi.condonacionPago(creditoId, pagoId, {
        accion: accionCondonacion,
        motivo: motivo.trim(),
        ...(!quitando && !condonaTodo ? { monto: montoCondonar } : {}),
      });
      terminar();
    } catch (e) {
      setError(e?.response?.data?.message
        || (quitando ? 'No se pudo quitar la condonación.' : 'No se pudo condonar la mora.'));
      setSubmitting(false);
    }
  }

  const filas = Array.isArray(preview?.filas) ? preview.filas : [];
  const condonacionRevertida = Number(preview?.condonacion_revertida || 0);
  const nombreCorto = nombreCliente(c.cliente);

  // Campo Motivo: idéntico en los tres modos (obligatorio, corto). El back lo
  // guarda en meta.reversion / meta.correcciones[] / meta.condonacion_historial[].
  const campoMotivo = (
    <div className={'cm-field' + (motivoErr ? ' cm-field-err' : '')}>
      <label>Motivo (obligatorio)</label>
      <input ref={motivoRef} className={'cm-input' + (motivoErr ? ' cm-input-err' : '')} type="text" maxLength={300} value={motivo}
        placeholder="Ej. el depósito nunca entró / contrato equivocado"
        onChange={(e) => { setMotivo(e.target.value); if (motivoErr) setMotivoErr(false); }} autoFocus />
      {motivoErr
        ? <div className="cm-hint-err"><i className="pi pi-exclamation-circle" style={{ fontSize: 12 }} />Escribe el motivo para poder guardar.</div>
        : <div className="cm-note">Queda guardado en el historial del pago, con tu nombre y la fecha.</div>}
    </div>
  );

  // Motivo vacío al confirmar: se marca el campo y se le da el foco en vez de
  // dejar el botón muerto. Devuelve true si hay que abortar.
  function faltaMotivo() {
    if (motivoOk) return false;
    setMotivoErr(true);
    setError(null);
    motivoRef.current?.focus();
    return true;
  }

  return (
    <div className="cart-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cart-modal">
        <div className="cm-head">
          <h3>{titulo}</h3>
          <button className="cm-close" onClick={onClose} disabled={submitting}><i className="pi pi-times" /></button>
        </div>
        <div className="cm-credit">
          <b>{c.cliente || '—'}</b> · {c.clave_contrato} · folio <b>{p.folio || '—'}</b> · {money(p.monto_pagado)}
          {p.fecha_pago ? ` · ${p.fecha_pago}` : ''}
        </div>

        {error && <div className="cm-error">{error}</div>}
        {/* Gate del back (LIFO, importado, mes cerrado, crédito liquidado…): su
            mensaje se pinta literal, sin traducirlo ni deducir nada aquí. */}
        {bloqueado && <div className="cm-error">{bloqueoMsg || 'Este pago no se puede modificar.'}</div>}

        {modo === 'revertir' ? (
          <>
            {previewLoading && <div className="cm-note" style={{ marginBottom: 12 }}><i className="pi pi-spinner pi-spin" style={{ marginRight: 6 }} />Calculando el efecto de deshacer…</div>}

            {preview && !bloqueado && (
              <div className="cm-result" style={{ background: '#FFFBEB', marginBottom: 14 }}>
                <div className="cr-folio" style={{ color: '#a4640a' }}>¿Deshacer este pago?</div>

                {/* Arranca con la frase que cualquiera entiende sin saber de cartera.
                    El detalle numérico va abajo, para quien quiera revisarlo. */}
                <div className="rv-lead">
                  Es como si {nombreCorto} nunca hubiera pagado los <b>{money(preview.pago?.monto_pagado)}</b>
                  {preview.pago?.fecha_pago ? <> del {fechaLarga(preview.pago.fecha_pago)}</> : null}.
                </div>

                {filas.length > 0 && (
                  <>
                    <div className="rv-sub">Lo que estaba pagado vuelve a deberse:</div>
                    <ul className="rv-list">
                      {filas.map((f, i) => {
                        // "Vuelve a deber X" = lo que quedará pendiente de esa cuota
                        // (su total menos lo que le quede abonado tras restaurar).
                        const debe = Math.max(0, Number(f.total || 0) - Number(f.a || 0));
                        const serv = Math.max(0, Number(f.servicios_de || 0) - Number(f.servicios_a || 0));
                        return (
                          <li key={i}>
                            <b>{f.periodo || f.etiqueta || `Cuota ${i + 1}`}</b>
                            {' → vuelve a deber '}
                            <b>{money(debe)}</b>
                            {/* Los servicios (seguro · placas · GPS) se cobran aparte
                                de la cuota y también se devuelven; sin esta parte el
                                desglose no suma el monto del pago. */}
                            {serv > 0 && <span className="rv-dim"> + {money(serv)} de servicios</span>}
                          </li>
                        );
                      })}
                      {/* La mora NO se restaura al valor viejo: se recalcula a hoy
                          (decisión §2.5 del plan). Se dice con todas sus letras. */}
                      {Number(preview.mora?.a || 0) > 0 && (
                        <li>
                          <b>Mora</b> → reaparece, hoy son <b>{money(preview.mora.a)}</b>
                        </li>
                      )}
                    </ul>
                  </>
                )}

                <div className="rv-lead" style={{ marginTop: 10 }}>
                  Su deuda sube de <b>{money(preview.saldo?.de)}</b> a <b>{money(preview.saldo?.a)}</b>.
                </div>

                {/* La mora casi siempre reaparece MÁS ALTA de lo que el pago cubrió
                    (siguieron corriendo los días). Sin explicarlo, se lee como error. */}
                {condonacionRevertida > 0 ? (
                  <div className="cm-note" style={{ marginTop: 6 }}>
                    Este pago perdonaba <b>{money(condonacionRevertida)}</b> de mora: ese perdón
                    también se deshace y la mora se vuelve a calcular con los días de atraso de hoy.
                  </div>
                ) : Number(preview.mora?.a || 0) > Number(preview.mora?.de || 0) && (
                  <div className="cm-note" style={{ marginTop: 6 }}>
                    La mora se vuelve a calcular con los días de atraso de hoy, por eso puede
                    reaparecer más alta de lo que el pago cubrió.
                  </div>
                )}

                <div className="cm-note" style={{ marginTop: 8 }}>
                  El recibo <b>{preview.pago?.folio || ''}</b> no se borra: queda en la lista marcado
                  como <b>Revertido</b>, con tu nombre y el motivo.
                  {preview.pago?.capturado_por
                    ? ` Lo registró ${preview.pago.capturado_por}${preview.pago.capturado_en ? ' el ' + fechaHora(preview.pago.capturado_en) : ''}.`
                    : ''}
                </div>
              </div>
            )}

            {campoMotivo}

            {/* Atajo: muchas veces el pago sí existe y solo el dato está mal. */}
            <div className="cm-quick">
              <button type="button" className="cm-chip" onClick={() => { setModo('corregir'); setError(null); }} disabled={submitting}>
                El depósito sí existe — solo corregir el dato
              </button>
            </div>

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
              <button className="btn btn-green btn-sm" onClick={confirmarRevertir}
                disabled={submitting || previewLoading || bloqueado || !preview}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-undo'} />
                {submitting ? 'Deshaciendo…' : 'Deshacer pago'}
              </button>
            </div>
          </>
        ) : modo === 'corregir' ? (
          <>
            <div className="cm-field">
              <label>Monto</label>
              <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                value={monto} onChange={(e) => setMonto(e.target.value)} />
              {cambiaMonto && (
                <div className="cm-note">= {money(montoNum)} · antes {money(montoOrig)}</div>
              )}
            </div>

            <div className="cm-row">
              <div className="cm-field">
                <label>Canal</label>
                <select className="cm-input" value={canal} onChange={(e) => setCanal(e.target.value)}>
                  {canales.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="cm-field">
                <label>Fecha</label>
                <input className="cm-input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </div>
            </div>

            {/* Mora: solo si ese día había. Se pregunta cuánto SE LE COBRÓ (así lo
                piensa el negocio: "solo le cobré $1,000 por un acuerdo") y lo demás
                se perdona. La referencia es la mora de AQUEL día, no la de hoy. */}
            {moraDelDia > 0 && (
              <div className="cm-field">
                <label>Mora de ese día · {money(moraDelDia)}</label>
                <div className="cm-seg" style={{ marginBottom: 8 }}>
                  <button type="button" className={cobradoNum >= moraDelDia - 0.004 ? 'on' : ''}
                    onClick={() => setCobrado(String(moraDelDia.toFixed(2)))}>Cobrada completa</button>
                  <button type="button" className={cobradoNum <= 0.004 ? 'on' : ''}
                    onClick={() => setCobrado('0')}>Condonada completa</button>
                  <button type="button"
                    className={cobradoNum > 0.004 && cobradoNum < moraDelDia - 0.004 ? 'on' : ''}
                    onClick={() => setCobrado(String((moraDelDia / 2).toFixed(2)))}>Solo una parte</button>
                </div>
                <input className="cm-input" type="text" inputMode="decimal"
                  value={cobrado === null ? String(cobradoOrig.toFixed(2)) : cobrado}
                  onChange={(e) => setCobrado(e.target.value)} />
                <div className="cm-note">
                  Se cobran <b>{money(cobradoNum)}</b> de mora
                  {condonarNum > 0.004 && <> y se condonan <b>{money(condonarNum)}</b></>}.
                  {cobradoNum > moraDelDia + 0.004 && (
                    <span style={{ color: '#b91c1c', fontWeight: 600 }}> No puede pasar de {money(moraDelDia)}.</span>
                  )}
                </div>
              </div>
            )}

            <div className="cm-field">
              <label>Notas</label>
              <textarea className="cm-input" value={notas} onChange={(e) => setNotas(e.target.value)}
                placeholder="Referencia bancaria, observaciones…" />
            </div>

            {/* Aviso fuerte: cambiar monto o fecha NO es editar un texto — el
                pago se deshace y se vuelve a aplicar desde cero. */}
            {reAplica && (
              <div className="cm-note" style={{ color: '#a4640a', fontWeight: 600, marginBottom: 12 }}>
                Cambiaste {cambiaMonto && cambiaFecha ? 'el monto y la fecha' : cambiaMonto ? 'el monto' : 'la fecha'}:
                {' '}el pago se vuelve a aplicar (las cuotas regresan a su estado previo y el dinero se reparte de nuevo).
                El folio {p.folio} no cambia.
              </div>
            )}
            {!reAplica && (cambiaCanal || cambiaNotas) && (
              <div className="cm-note" style={{ marginBottom: 12 }}>
                Solo cambian datos del recibo: no se tocan cuotas, saldo ni mora.
              </div>
            )}

            {campoMotivo}

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
              <button className="btn btn-green btn-sm" onClick={confirmarCorregir}
                disabled={submitting || previewLoading || bloqueado}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'} />
                {submitting ? 'Guardando…' : 'Guardar corrección'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Condonación: acción contable de 1 clic. No toca cuotas ni saldo,
                por eso no lleva vista previa ni exige ser el último pago. */}
            <div className="cm-result" style={{ background: '#FFFBEB', marginBottom: 14 }}>
              {quitando ? (
                <>
                  <div className="cr-row">
                    <span>Condonación de este pago</span>
                    <b style={{ color: '#a4640a' }}>{money(condonadoAqui)}</b>
                  </div>
                  <div className="cm-note">
                    Se le devuelve al crédito y la mora vuelve a correr desde su vencimiento
                    (se recalcula a hoy). No se tocan cuotas ni saldo.
                  </div>
                </>
              ) : (
                <>
                  <div className="cr-row">
                    <span>Mora acumulada del crédito</span>
                    <b style={{ color: '#a4640a' }}>{money(mora)}</b>
                  </div>
                  <div className="cm-note">
                    {condonaTodo
                      ? 'Se condona completa y queda ligada a este pago. No se tocan cuotas ni saldo.'
                      : <>Se condonan <b>{money(montoCondonar)}</b> y el crédito sigue con <b>{money(Math.max(0, Number(mora) - montoCondonar))}</b> de mora. No se tocan cuotas ni saldo.</>}
                  </div>
                </>
              )}
            </div>

            {/* Perdón parcial: a veces el negocio perdona una parte y cobra el
                resto. Vacío = toda la mora (el back lo interpreta así). */}
            {!quitando && (
              <div className="cm-field">
                <label>¿Cuánto se condona?</label>
                <div className="cm-seg" style={{ marginBottom: 8 }}>
                  <button type="button" className={condonaTodo ? 'on' : ''}
                    onClick={() => setParcial('')}>Toda la mora</button>
                  <button type="button" className={!condonaTodo ? 'on' : ''}
                    onClick={() => setParcial(String(Number(mora || 0).toFixed(2)))}>Solo una parte</button>
                </div>
                {!condonaTodo && (
                  <>
                    <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                      value={parcial} onChange={(e) => setParcial(e.target.value)} />
                    {montoCondonar > Number(mora || 0) + 0.005 && (
                      <div className="cm-note" style={{ color: '#b91c1c', fontWeight: 600 }}>
                        No se puede condonar más de la mora acumulada ({money(mora)}).
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {campoMotivo}

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
              <button className="btn btn-green btn-sm" onClick={confirmarCondonacion} disabled={submitting}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : (quitando ? 'pi pi-ban' : 'pi pi-percentage')} />
                {submitting
                  ? (quitando ? 'Quitando…' : 'Condonando…')
                  : (quitando ? 'Quitar condonación' : 'Condonar mora')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RevertirPagoModal;
